/**
 * Marketplace Settings Component
 * Thin shell: header + tab navigation + delegates to sub-components
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Save,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  Globe,
  Columns,
  Trash2,
  DollarSign,
  FileSpreadsheet,
} from 'lucide-react';
import type { CustomMarketplace, CategoryColumnDefaults } from '../../types/database';
import type { Product } from '../../types';
import type { SettingsTab } from './types';
import type { StoredExpense } from './AmazonExpensesTab';
import AmazonExpensesTab from './AmazonExpensesTab';
import GeneralSettingsTab from './settings/GeneralSettingsTab';
import CountriesTab from './settings/CountriesTab';
import ColumnsTab from './settings/ColumnsTab';
import ExpensesTab from './settings/ExpensesTab';
import { apiBulkUpsertCategoryColumnDefaults, apiGetCategoryDefaultsByMarketplace } from '../../services/api';

interface MarketplaceSettingsProps {
  selectedMarketplace: CustomMarketplace;
  products: Product[];
  categoryDefaults: CategoryColumnDefaults[];
  settingsTab: SettingsTab;
  isSaving: boolean;
  importStatus: { success: number; errors: number } | null;

  // Setters
  setSelectedMarketplace: (mp: CustomMarketplace) => void;
  setSettingsTab: (tab: SettingsTab) => void;
  setCategoryDefaults: (defaults: CategoryColumnDefaults[] | ((prev: CategoryColumnDefaults[]) => CategoryColumnDefaults[])) => void;
  setViewMode: (mode: 'grid' | 'table' | 'settings') => void;
  setSelectedCountry: (code: string) => void;

  // Handlers
  onSave: () => Promise<void>;
  onDelete: () => Promise<void>;
  onBackToGrid: () => void;
}

const MarketplaceSettings: React.FC<MarketplaceSettingsProps> = ({
  selectedMarketplace,
  products,
  categoryDefaults,
  settingsTab,
  isSaving,
  importStatus,
  setSelectedMarketplace,
  setSettingsTab,
  setCategoryDefaults,
  setViewMode,
  setSelectedCountry,
  onSave,
  onDelete,
  onBackToGrid,
}) => {
  // Amazon Expenses V2 state
  const [amazonExpenses, setAmazonExpenses] = useState<StoredExpense[]>(() => {
    const saved = localStorage.getItem(`amazonExpenses_${selectedMarketplace.id}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as StoredExpense[];
        return parsed.map(e => ({ ...e, updatedAt: new Date(String(e.updatedAt)) }));
      } catch {
        return [];
      }
    }
    return [];
  });

  // Sync amazonExpenses to DB (category_column_defaults)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialSyncDone = useRef(false);

  const syncExpensesToDB = useCallback(async (exps: StoredExpense[]) => {
    if (exps.length === 0) return;

    try {
      const dbDefaults = exps.map(e => ({
        marketplaceId: selectedMarketplace.id,
        countryCode: e.countryCode,
        category: e.category,
        fulfillmentType: e.fulfillmentType,
        columnDefaults: {
          sellingPercent: e.sellingFeePercent,
          fbaFeePercent: e.fbaFeePercent,
          adsPercent: e.advertisingPercent,
          refundPercent: e.refundLossPercent,
          fbaCostPercent: e.fbaCostPercent || 0,
          fbmCostPercent: e.fbmCostPercent || 0,
          vatPercent: e.vatPercent || 0,
          gstPercent: e.gstPercent || 0,
          customsPercent: e.customsPercent || 0,
          ddpPercent: e.ddpPercent || 0,
          shipPercent: e.shipPercent || 0,
          warehousePercent: e.warehousePercent || 0,
        },
        targetMargin: e.marginPercent || 30,
        periodStart: e.periodStart || null,
        periodEnd: e.periodEnd || null,
        sampleSize: e.sampleSize || 0,
        dataSource: e.dataSource,
      }));

      await apiBulkUpsertCategoryColumnDefaults(dbDefaults);

      // Refresh categoryDefaults from DB
      const fresh = await apiGetCategoryDefaultsByMarketplace(selectedMarketplace.id);
      if (fresh) {
        const mapped: CategoryColumnDefaults[] = (fresh as Record<string, unknown>[]).map((r: Record<string, unknown>) => ({
          id: r.id as string,
          marketplaceId: (r.marketplace_id || r.marketplaceId) as string,
          countryCode: (r.country_code || r.countryCode) as string,
          category: r.category as string,
          fulfillmentType: (r.fulfillment_type || r.fulfillmentType) as string,
          targetMargin: Number(r.target_margin ?? r.targetMargin ?? 30),
          columnDefaults: (r.column_defaults || r.columnDefaults || {}) as Record<string, number | string>,
          sampleSize: Number(r.sample_size ?? r.sampleSize ?? 0),
          dataSource: (r.data_source || r.dataSource || 'manual') as 'manual' | 'calculated' | 'imported',
          periodStart: r.period_start ? new Date(r.period_start as string) : undefined,
          periodEnd: r.period_end ? new Date(r.period_end as string) : undefined,
          createdAt: new Date((r.created_at || r.createdAt) as string),
          updatedAt: new Date((r.updated_at || r.updatedAt) as string),
        }));
        setCategoryDefaults(mapped);
      }
    } catch {
      // Silent fail — data is still in localStorage
    }
  }, [selectedMarketplace.id, setCategoryDefaults]);

  // Save amazonExpenses to localStorage + DB sync
  React.useEffect(() => {
    localStorage.setItem(`amazonExpenses_${selectedMarketplace.id}`, JSON.stringify(amazonExpenses));

    if (amazonExpenses.length > 0) {
      if (!initialSyncDone.current) {
        // First mount: sync immediately (migrate existing localStorage → DB)
        initialSyncDone.current = true;
        syncExpensesToDB(amazonExpenses);
      } else {
        // Subsequent changes: debounce 1s
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => syncExpensesToDB(amazonExpenses), 1000);
      }
    }

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [amazonExpenses, selectedMarketplace.id, syncExpensesToDB]);

  // Get unique categories from products (needed for AmazonExpensesTab)
  const categories = React.useMemo(() => {
    const cats = new Set<string>();
    products.forEach(p => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToGrid}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <span className="text-2xl">{selectedMarketplace.icon}</span>
            <div>
              <h2 className="font-semibold text-slate-800">{selectedMarketplace.name}</h2>
              <p className="text-xs text-slate-500">Ayarlar</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {importStatus && (
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                importStatus.errors > 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
              }`}>
                {importStatus.errors > 0 ? (
                  <AlertCircle className="w-4 h-4" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                <span>
                  {importStatus.errors > 0 ? 'Hata!' : 'Kaydedildi'}
                </span>
              </div>
            )}
            <button
              onClick={onDelete}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </button>
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-slate-200">
          <nav className="flex px-4" aria-label="Tabs">
            {[
              { key: 'general', label: 'Genel', icon: Globe },
              { key: 'countries', label: '\u00DClkeler', icon: Globe },
              { key: 'columns', label: 'S\u00FCtunlar', icon: Columns },
              { key: 'amazonExpenses', label: 'Amazon Masraflar\u0131', icon: DollarSign },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setSettingsTab(tab.key as SettingsTab)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  settingsTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {settingsTab === 'general' && (
            <GeneralSettingsTab
              selectedMarketplace={selectedMarketplace}
              setSelectedMarketplace={setSelectedMarketplace}
            />
          )}

          {settingsTab === 'countries' && (
            <CountriesTab
              selectedMarketplace={selectedMarketplace}
              setSelectedMarketplace={setSelectedMarketplace}
              setCategoryDefaults={setCategoryDefaults}
            />
          )}

          {settingsTab === 'columns' && (
            <ColumnsTab
              selectedMarketplace={selectedMarketplace}
              setSelectedMarketplace={setSelectedMarketplace}
            />
          )}

          {settingsTab === 'expenses' && (
            <ExpensesTab
              selectedMarketplace={selectedMarketplace}
              products={products}
              categoryDefaults={categoryDefaults}
              setCategoryDefaults={setCategoryDefaults}
            />
          )}

          {settingsTab === 'amazonExpenses' && (
            <AmazonExpensesTab
              selectedMarketplace={selectedMarketplace}
              expenses={amazonExpenses}
              setExpenses={setAmazonExpenses}
              categories={categories}
            />
          )}
        </div>
      </div>

      {/* Back to table button */}
      {selectedMarketplace.countries.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={() => {
              const country = selectedMarketplace.countries.find(c => c.isActive) || selectedMarketplace.countries[0];
              setSelectedCountry(country.code);
              setViewMode('table');
            }}
            className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Fiyat Tablosuna Git
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketplaceSettings;
